import React, { useEffect, useRef, useState, memo } from 'react';
import 'molstar/build/viewer/molstar.css';

interface PDBViewerProps {
  pdb_id: string;
}

const PDBViewer = memo(({ pdb_id }: PDBViewerProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<any>(null);
  const [viewerReady, setViewerReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const loadedPdbRef = useRef<string | null>(null);

  // 1. Create viewer once on mount, dispose on unmount
  useEffect(() => {
    let disposed = false;

    (async () => {
      if (!containerRef.current || typeof window === 'undefined') return;

      try {
        const { Viewer } = await import('molstar/lib/apps/viewer/app');
        if (disposed) return;

        const viewer = await Viewer.create(containerRef.current, {
          layoutIsExpanded: false,
          layoutShowControls: true,
          layoutShowRemoteState: false,
          layoutShowSequence: true,
          layoutShowLog: false,
          layoutShowLeftPanel: false,
          collapseLeftPanel: true,
          collapseRightPanel: true,
          viewportShowExpand: false,
          viewportShowSelectionMode: false,
          viewportShowAnimation: true,
          pdbProvider: 'rcsb',
        });

        if (disposed) { viewer.dispose(); return; }

        // Disable fog/clipping, keep default Mol* theme colors
        try {
          viewer.plugin.canvas3d?.setProps({
            cameraFog: { name: 'off', params: {} },
            cameraClipping: { far: false, minNear: 0.1 },
          });
        } catch (_) { /* ignore props failure */ }

        viewerRef.current = viewer;
        if (!disposed) setViewerReady(true);
      } catch (err) {
        console.error('Mol* init failed:', err);
        if (!disposed) {
          setError('Failed to initialize 3D viewer');
          setIsLoading(false);
        }
      }
    })();

    return () => {
      disposed = true;
      if (viewerRef.current) {
        viewerRef.current.dispose();
        viewerRef.current = null;
      }
    };
  }, []);

  // 2. Load / swap PDB when viewer is ready or pdb_id changes
  useEffect(() => {
    if (!viewerReady || !viewerRef.current || !pdb_id) {
      if (!pdb_id) setIsLoading(false);
      return;
    }
    if (loadedPdbRef.current === pdb_id) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    (async () => {
      try {
        await viewerRef.current.plugin.clear();
        await viewerRef.current.loadPdb(pdb_id.toLowerCase());
        if (!cancelled) {
          loadedPdbRef.current = pdb_id;
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Failed to load PDB:', pdb_id, err);
        if (!cancelled) {
          setError(`Could not load structure ${pdb_id}`);
          setIsLoading(false);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [viewerReady, pdb_id]);

  // 3. Responsive resize
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      viewerRef.current?.plugin?.canvas3d?.handleResize();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <style>{`
        .molstar-wrapper .msp-plugin {
          position: absolute !important;
          inset: 0;
          border: none !important;
          border-radius: 6px;
          overflow: hidden;
        }
        .molstar-wrapper .msp-viewport-controls {
          right: 6px !important;
          bottom: 6px !important;
        }
        .molstar-wrapper .msp-sequence-wrapper {
          font-family: 'Courier New', Courier, monospace !important;
          overflow-x: hidden !important;
          overflow-y: auto !important;
          white-space: normal !important;
          word-break: break-all !important;
          max-height: none !important;
          min-height: 60px !important;
        }
        .molstar-wrapper .msp-sequence-wrapper * {
          font-family: 'Courier New', Courier, monospace !important;
          line-height: 1.4 !important;
          letter-spacing: normal !important;
          box-sizing: content-box !important;
        }
        .molstar-wrapper .msp-sequence-chain-label,
        .molstar-wrapper .msp-sequence-present,
        .molstar-wrapper .msp-sequence-missing {
          font-size: 12px !important;
          display: inline !important;
        }
      `}</style>

      <div
        className="molstar-wrapper w-100 h-100"
        style={{
          position: 'relative',
          background: 'transparent',
          borderRadius: '6px',
          overflow: 'hidden',
          minHeight: '500px',
        }}
      >
        <div
          ref={containerRef}
          style={{ width: '100%', height: '100%', position: 'absolute', inset: 0 }}
        />

        {isLoading && !error && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(51, 51, 51, 0.85)',
              zIndex: 100,
              gap: '0.75rem',
              pointerEvents: 'none',
            }}
          >
            <div
              className="spinner-border"
              style={{ color: '#3b82f6', width: '2rem', height: '2rem', borderWidth: '3px' }}
              role="status"
            >
              <span className="visually-hidden">Loading…</span>
            </div>
            <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>
              Loading {pdb_id}…
            </span>
          </div>
        )}

        {error && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(51, 51, 51, 0.9)',
              zIndex: 100,
              color: '#c0c0c0',
              fontSize: '0.8125rem',
              padding: '1rem',
              textAlign: 'center',
            }}
          >
            {error}
          </div>
        )}

        {!pdb_id && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#c0c0c0',
              fontSize: '0.8125rem',
            }}
          >
            No structure available
          </div>
        )}
      </div>
    </>
  );
});

PDBViewer.displayName = 'PDBViewer';

export default PDBViewer;
